import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { EventForm } from '../components/EventForm';
import { EventCard } from '../components/EventCard';
import StreamManagement from '../components/StreamManagement';
import ActiveStreamsManagement from '../components/ActiveStreamsManagement';
import SiteVisitsAnalytics from '../components/SiteVisitsAnalytics';
import PermissionManagement from '../components/PermissionManagement';
import UserPermissionsModal from '../components/UserPermissionsModal';
import FooterManagement from '../components/FooterManagement';
import { AdSense } from '../components/AdSense';
import '../styles/AdminPage.css';
import '../styles/AdminDashboard.css';

export function AdminDashboard() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [streams, setStreams] = useState([]);
  const [logs, setLogs] = useState([]);
  const [siteVisits, setSiteVisits] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [filterEventId, setFilterEventId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [scannerPermissionUserIds, setScannerPermissionUserIds] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMethod, setInviteMethod] = useState('email');
  const [registrationLink, setRegistrationLink] = useState('');
  const [registrationSearchQuery, setRegistrationSearchQuery] = useState('');
  const [showEventInviteModal, setShowEventInviteModal] = useState(false);
  const [selectedEventForInvite, setSelectedEventForInvite] = useState('');
  const [selectedUsersForInvite, setSelectedUsersForInvite] = useState([]);
  const [showUserPermissionsModal, setShowUserPermissionsModal] = useState(false);
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState(null);
  const [guestEmailForInvite, setGuestEmailForInvite] = useState('');
  const [eventInviteLink, setEventInviteLink] = useState('');
  
  // Media Library States
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [showMediaForm, setShowMediaForm] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [mediaFormData, setMediaFormData] = useState({
    videoName: '',
    videoLink: '',
    videoId: '',
    mediaType: 'video'
  });
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all');
  const [mediaSortKey, setMediaSortKey] = useState('type');
  const [mediaSortDirection, setMediaSortDirection] = useState('asc');
  const [mediaSearchTerm, setMediaSearchTerm] = useState('');
  const [mediaSearchQuery, setMediaSearchQuery] = useState('');
  const [mediaPage, setMediaPage] = useState(1);
  const MEDIA_ROWS_PER_PAGE = 50;

  // Normalize media items coming from API (handle different backend field names)
  const normalizeMediaItem = (raw) => {
    if (!raw) return null;
    const id = raw.id ?? raw._id ?? raw.mediaId ?? raw.mid ?? raw.id_str ?? raw._id_str ?? Date.now();

    // Helper to find a key by regex and return its value
    const findByKeyRegex = (obj, re) => {
      if (!obj || typeof obj !== 'object') return undefined;
      for (const k of Object.keys(obj)) {
        if (re.test(k)) return obj[k];
      }
      return undefined;
    };

    const videoName = raw.videoName ?? raw.name ?? raw.title ?? raw.label ?? raw.video_name ?? raw.media_name ?? findByKeyRegex(raw, /video[_\s-]?name/i) ?? findByKeyRegex(raw, /name/i) ?? '';
    let videoLink = raw.videoLink ?? raw.link ?? raw.url ?? raw.video_url ?? raw.video_link ?? raw.source ?? raw.path ?? raw.file ?? raw.location ?? (raw.meta && raw.meta.url) ?? (raw.video && raw.video.url) ?? findByKeyRegex(raw, /video[_\s-]?(link|url|path)/i) ?? '';
    // If the link is nested in an object, try to extract a string
    if (videoLink && typeof videoLink === 'object') {
      if (Array.isArray(videoLink)) {
        videoLink = videoLink.find(v => typeof v === 'string') || '';
      } else {
        const strVal = Object.values(videoLink).find(v => typeof v === 'string');
        videoLink = strVal || '';
      }
    }
    const videoId = raw.videoId ?? raw.video_id ?? raw.videoID ?? raw.ytid ?? findByKeyRegex(raw, /video[_\s-]?(id|vid)/i) ?? '';
    const mediaType = raw.mediaType ?? raw.type ?? raw.media_type ?? 'video';
    return { ...raw, id, videoName, videoLink: String(videoLink || ''), videoId, mediaType };
  };

  const normalizeMediaArray = (arr) => (Array.isArray(arr) ? arr.map(normalizeMediaItem).filter(Boolean) : []);

  const refreshMediaList = async (headers) => {
    try {
      const mediaResponse = await fetch('/api/media/admin/all', { headers });
      const mediaData = await mediaResponse.json();
      if (mediaData.media && Array.isArray(mediaData.media)) {
        setMediaLibrary(normalizeMediaArray(mediaData.media));
      } else if (Array.isArray(mediaData)) {
        setMediaLibrary(normalizeMediaArray(mediaData));
      } else {
        setMediaLibrary([]);
      }
    } catch (err) {
      console.error('Error refreshing media list:', err);
    }
  };

  useEffect(() => {
    // Check if user is admin
    const role = localStorage.getItem('user_role');
    const token = localStorage.getItem('session_token');
    
    if (role !== 'admin' || !token) {
      navigate('/login', { replace: true });
      return;
    }

    // Load user email
    const email = localStorage.getItem('user_email');
    if (email) {
      setUserEmail(email);
    }

    // Headers with session token for authenticated requests
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-Token': token,
    };

    // Fetch all data
    Promise.all([
      fetch('/api/events/admin/all', { headers }).then(r => r.json()).catch(() => ({ events: [] })),
      fetch('/api/users/admin/all', { headers }).then(r => r.json()).catch(() => ({ users: [] })),
      fetch('/api/registrations/admin/all', { headers }).then(r => r.json()).catch(() => ({ registrations: [] })),
      fetch('/api/streams/admin/all', { headers }).then(r => r.json()).catch(() => ({ streams: [] })),
      fetch('/api/logs/admin/all', { headers }).then(r => r.json()).catch(() => ({ logs: [] })),
      fetch('/api/analytics/site-visits', { headers }).then(r => r.json()).catch(() => ({ total: 0 })),
      fetch('/api/media/admin/all', { headers }).then(r => r.json()).catch(() => ({ media: [] }))
    ])
      .then(([eventsData, usersData, registrationsData, streamsData, logsData, visitsData, mediaData]) => {
        // Check for unauthorized access across any endpoints
        const isUnauthorized = [eventsData, usersData, registrationsData, streamsData, logsData, visitsData, mediaData]
          .some(data => data && data.error === 'Unauthorized');

        if (isUnauthorized) {
          console.error('Session expired or unauthorized. Redirecting to login.');
          localStorage.removeItem('session_token');
          navigate('/login', { replace: true });
          return;
        }

        // Events
        if (Array.isArray(eventsData)) {
          setEvents(eventsData);
        } else if (eventsData.events && Array.isArray(eventsData.events)) {
          setEvents(eventsData.events);
        }

        // Users
        if (usersData.users && Array.isArray(usersData.users)) {
          setUsers(usersData.users);
        }

        // Registrations
        if (registrationsData.registrations && Array.isArray(registrationsData.registrations)) {
          setRegistrations(registrationsData.registrations);
        }

        // Streams
        if (streamsData.streams && Array.isArray(streamsData.streams)) {
          setStreams(streamsData.streams);
        }

        // Logs
        if (logsData.logs && Array.isArray(logsData.logs)) {
          setLogs(logsData.logs);
        } else if (Array.isArray(logsData)) {
          setLogs(logsData);
        }

        // Site Visits
        if (visitsData.total) {
          setSiteVisits(visitsData.total);
        } else if (typeof visitsData === 'number') {
          setSiteVisits(visitsData);
        }

        // Media Library
        if (mediaData.media && Array.isArray(mediaData.media)) {
          setMediaLibrary(normalizeMediaArray(mediaData.media));
        } else if (Array.isArray(mediaData)) {
          setMediaLibrary(normalizeMediaArray(mediaData));
        }

        // Note: scanner badge now derives from each user's `permissions` array when available.
        // Keep `scannerPermissionUserIds` state as fallback if backend doesn't attach permissions to user objects.

        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching data:', err);
        setLoading(false);
      });
  }, [navigate]);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      try {
        const sessionToken = localStorage.getItem('session_token');
        if (sessionToken) {
          await fetch('/api/logout', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Session-Token': sessionToken,
            },
          });
        }
      } catch (error) {
        console.error('Logout error:', error);
      }

      // Clear session
      localStorage.clear();
      sessionStorage.clear();
      
      navigate('/login', { replace: true });
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (window.confirm(`Are you sure you want to delete user ${userEmail}? This action cannot be undone.`)) {
      const token = localStorage.getItem('session_token');
      const headers = {
        'Content-Type': 'application/json',
        'X-Session-Token': token,
      };

      try {
        const response = await fetch(`/api/users/${userId}`, {
          method: 'DELETE',
          headers,
        });

        if (!response.ok) {
          const error = await response.json();
          setErrorMessage(error.message || 'Failed to delete user');
          return;
        }

        setUsers(users.filter(u => u.id !== userId));
        setSuccessMessage('✅ User deleted successfully!');
        setErrorMessage('');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        console.error('Delete user error:', err);
        setErrorMessage(err.message || 'Failed to delete user');
      }
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({ ...user });
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    const token = localStorage.getItem('session_token');
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-Token': token,
    };

    try {
      const response = await fetch(`/api/users/${userId}/role`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        const error = await response.json();
        setErrorMessage(error.message || 'Failed to update user role');
        return;
      }

      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      setSuccessMessage(`✅ User role updated to ${newRole}!`);
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Update role error:', err);
      setErrorMessage(err.message || 'Failed to update user role');
    }
  };

  const refreshScannerPermissionUserIds = async () => {
    const token = localStorage.getItem('session_token');
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-Token': token,
    };

    try {
      const response = await fetch('/api/permissions/name/ticket.scanner/users', { headers });
      const data = await response.json();
      if (data.success && Array.isArray(data.users)) {
        setScannerPermissionUserIds(data.users.map(user => user.id));
      } else {
        setScannerPermissionUserIds([]);
      }
    } catch (err) {
      console.error('Error refreshing scanner permission users:', err);
    }
  };

  const openUserPermissions = (user) => {
    setSelectedUserForPermissions(user);
    setShowUserPermissionsModal(true);
  };

  const handleGrantScannerAccess = async (userId) => {
    const token = localStorage.getItem('session_token');
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-Token': token,
    };

    try {
      const response = await fetch(`/api/users/${userId}/permissions/grant`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ permission_name: 'ticket.scanner' }),
      });

      if (!response.ok) {
        const error = await response.json();
        setErrorMessage(error.message || 'Failed to grant scanner access');
        return;
      }

      setScannerPermissionUserIds(prev => [...new Set([...prev, userId])]);
      setSuccessMessage('✅ Scanner access granted successfully.');
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Grant scanner access error:', err);
      setErrorMessage(err.message || 'Failed to grant scanner access');
    }
  };

  const handleRevokeScannerAccess = async (userId) => {
    if (!window.confirm('Revoke ticket scanner permission from this user?')) {
      return;
    }

    const token = localStorage.getItem('session_token');
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-Token': token,
    };

    try {
      const response = await fetch(`/api/users/${userId}/permissions/revoke`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ permission_name: 'ticket.scanner' }),
      });

      if (!response.ok) {
        const error = await response.json();
        setErrorMessage(error.message || 'Failed to revoke scanner access');
        return;
      }

      setScannerPermissionUserIds(prev => prev.filter(id => id !== userId));
      setSuccessMessage('✅ Scanner access revoked successfully.');
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Revoke scanner access error:', err);
      setErrorMessage(err.message || 'Failed to revoke scanner access');
    }
  };

  const handleSaveUserChanges = async (e) => {
    e.preventDefault();
    
    if (!editingUser.name || !editingUser.email) {
      setErrorMessage('Name and email are required');
      return;
    }

    const token = localStorage.getItem('session_token');
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-Token': token,
    };

    try {
      const response = await fetch(`/api/users/${editingUser.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          name: editingUser.name,
          email: editingUser.email,
          role: editingUser.role,
          isActive: editingUser.isActive,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        setErrorMessage(error.message || 'Failed to update user');
        return;
      }

      const updatedUser = await response.json();
      setUsers(users.map(u => u.id === editingUser.id ? updatedUser : u));
      setSuccessMessage('✅ User information updated successfully!');
      setErrorMessage('');
      setEditingUser(null);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Update user error:', err);
      setErrorMessage(err.message || 'Failed to update user');
    }
  };

  const handleDeleteRegistration = (registrationId) => {
    if (window.confirm('Are you sure you want to delete this registration?')) {
      const token = localStorage.getItem('session_token');
      const headers = {
        'Content-Type': 'application/json',
        'X-Session-Token': token,
      };

      fetch(`/api/registrations/${registrationId}`, {
        method: 'DELETE',
        headers,
      })
        .then(r => r.json())
        .then(() => {
          setRegistrations(registrations.filter(r => r.id !== registrationId));
          setSuccessMessage('✅ Registration deleted successfully!');
          setErrorMessage('');
          setTimeout(() => setSuccessMessage(''), 3000);
        })
        .catch(err => {
          console.error('Delete error:', err);
          setErrorMessage(err.message || 'Failed to delete registration');
        });
    }
  };

  const getFilteredRegistrations = () => {
    const query = registrationSearchQuery.toLowerCase();
    return registrations.filter(reg => {
      const matchesSearch = !registrationSearchQuery || 
        reg.userEmail?.toLowerCase().includes(query) ||
        reg.firstName?.toLowerCase().includes(query) ||
        reg.lastName?.toLowerCase().includes(query) ||
        reg.eventName?.toLowerCase().includes(query);
      
      const matchesEvent = !filterEventId || reg.eventId === filterEventId;
      const matchesStatus = !filterStatus || reg.status === filterStatus;
      return matchesSearch && matchesEvent && matchesStatus;
    });
  };

  const handleUpdateRegistrationStatus = async (registrationId, newStatus) => {
    const token = localStorage.getItem('session_token');
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-Token': token,
    };

    try {
      const response = await fetch(`/api/registrations/${registrationId}/status`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        const error = await response.json();
        setErrorMessage(error.message || 'Failed to update registration status');
        return;
      }

      setRegistrations(registrations.map(r => 
        r.id === registrationId ? { ...r, status: newStatus } : r
      ));
      setSuccessMessage(`✅ Registration status updated to ${newStatus}!`);
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Update status error:', err);
      setErrorMessage(err.message || 'Failed to update registration status');
    }
  };

  const handleToggleAdminApproval = async (registrationId, isApproved) => {
    const token = localStorage.getItem('session_token');
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-Token': token,
    };

    try {
      const response = await fetch(`/api/registrations/${registrationId}/verify`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ isApproved }),
      });

      if (!response.ok) {
        const error = await response.json();
        setErrorMessage(error.message || 'Failed to update approval status');
        return;
      }

      const result = await response.json();
      const updatedRegistration = result.registration;
      setRegistrations(registrations.map(r => 
        r.id === registrationId ? { ...r, isAdminApproved: updatedRegistration ? updatedRegistration.admin_approved === 1 : isApproved } : r
      ));
      setSuccessMessage(`✅ Registration ${isApproved ? 'approved' : 'unapproved'}!`);
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 1000);
    } catch (err) {
      console.error('Approval error:', err);
      setErrorMessage(err.message || 'Failed to update approval status');
    }
  };

  const handleToggleTicketVerification = async (registrationId, isVerified) => {
    const token = localStorage.getItem('session_token');
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-Token': token,
    };

    try {
      const response = await fetch(`/api/registrations/${registrationId}/ticket-verify`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ isVerified }),
      });

      if (!response.ok) {
        const error = await response.json();
        setErrorMessage(error.message || 'Failed to update ticket verification status');
        return;
      }

      const result = await response.json();
      const updatedRegistration = result.registration;
      setRegistrations(registrations.map(r => 
        r.id === registrationId ? { ...r, isVerified: updatedRegistration ? updatedRegistration.verify_ticket_number === 1 : isVerified } : r
      ));
      setSuccessMessage(`✅ Ticket ${isVerified ? 'verified' : 'unverified'}!`);
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 1000);
    } catch (err) {
      console.error('Ticket verification error:', err);
      setErrorMessage(err.message || 'Failed to update ticket verification status');
    }
  };

  const handleToggleAttendance = async (registrationId, attended) => {
    const token = localStorage.getItem('session_token');
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-Token': token,
    };

    try {
      const response = await fetch(`/api/registrations/${registrationId}/attendance`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ attended }),
      });

      if (!response.ok) {
        const error = await response.json();
        setErrorMessage(error.message || 'Failed to update attendance');
        return;
      }

      setRegistrations(registrations.map(r => 
        r.id === registrationId ? { ...r, attended } : r
      ));
      setSuccessMessage(`✅ Attendance ${attended ? 'recorded' : 'removed'}!`);
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Attendance error:', err);
      setErrorMessage(err.message || 'Failed to update attendance');
    }
  };

  const handleExportRegistrationsCSV = () => {
    if (registrations.length === 0) {
      setErrorMessage('No registrations to export');
      return;
    }

    // Prepare CSV data
    const headers = ['Event', 'User Email', 'Registration Date', 'Status', 'Ticket Verified', 'Attended'];
    const rows = getFilteredRegistrations().map(reg => [
      reg.eventName || 'N/A',
      reg.userEmail,
      reg.registeredAt ? new Date(reg.registeredAt).toLocaleDateString() : 'N/A',
      reg.status || 'registered',
      reg.isVerified ? 'Yes' : 'No',
      reg.attended ? 'Yes' : 'No',
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => 
        row.map(cell => `"${cell}"`).join(',')
      ),
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `registrations_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    setSuccessMessage('✅ Registrations exported to CSV!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleCreateEvent = (formData) => {
    setEvents([...events, { ...formData, id: Date.now() }]);
    setSuccessMessage('Event created successfully!');
    setShowForm(false);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleUpdateEvent = (updatedEvent) => {
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
    setSuccessMessage('Event updated successfully!');
    setShowForm(false);
    setEditingEvent(null);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleDeleteEvent = (eventId) => {
    setEvents(events.filter(e => e.id !== eventId));
    setSuccessMessage('Event deleted successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setShowForm(true);
  };

  const handleDelete = (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      handleDeleteEvent(eventId);
    }
  };

  const getFilteredUsers = () => {
    return users.filter(user => {
      const query = userSearchQuery.toLowerCase();
      return (
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
      );
    });
  };

  const userHasPermission = (user, permissionName) => {
    if (!user) return false;
    // If the user object has a `permissions` array, prefer that
    if (Array.isArray(user.permissions) && user.permissions.length > 0) {
      return user.permissions.some(p => (p.permission_name || p.name || p) === permissionName);
    }
    // Some APIs attach permission names under `userPermissions` or similar
    if (Array.isArray(user.userPermissions) && user.userPermissions.length > 0) {
      return user.userPermissions.some(p => (p.permission_name || p.name || p) === permissionName);
    }
    // Fallback: use the scannerPermissionUserIds set (kept for backward compat)
    return scannerPermissionUserIds.includes(user.id);
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    
    if (!inviteEmail || !inviteEmail.includes('@')) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    const token = localStorage.getItem('session_token');
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-Token': token,
    };

    try {
      const response = await fetch('/api/users/invite', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: inviteEmail,
          inviteMethod: inviteMethod,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        setErrorMessage(error.message || 'Failed to send invite');
        return;
      }

      const data = await response.json();
      
      if (inviteMethod === 'email') {
        setSuccessMessage(`✅ Invitation sent successfully to ${inviteEmail}!`);
        setErrorMessage('');
        setInviteEmail('');
        setShowInviteModal(false);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        // Show the link for copying
        setRegistrationLink(data.link || '');
        setSuccessMessage('✅ Registration link generated!');
        setErrorMessage('');
      }
    } catch (err) {
      console.error('Invite error:', err);
      setErrorMessage(err.message || 'Failed to send invitation');
    }
  };

  const generateInviteLink = (eventId, email) => {
    // Generate link with email parameter for guest registration
    const params = new URLSearchParams();
    if (email) {
      params.append('email', email);
    }
    const registrationLink = `${window.location.origin}/events/${eventId}/register${email ? `?${params.toString()}` : ''}`;
    return registrationLink;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccessMessage('✅ Link copied to clipboard!');
      setTimeout(() => setSuccessMessage(''), 2000);
    });
  };

  // Media Library Functions
  const handleAddMedia = async (e) => {
    e.preventDefault();

    if (!mediaFormData.videoName) {
      setErrorMessage('Please enter a media name');
      return;
    }

    if (!mediaFormData.mediaType) {
      setErrorMessage('Please select a media type');
      return;
    }

    const token = localStorage.getItem('session_token');
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-Token': token,
    };

    try {
      const endpoint = editingMedia ? `/api/media/${editingMedia.id}` : '/api/media';
      const method = editingMedia ? 'PUT' : 'POST';

      const response = await fetch(endpoint, {
        method,
        headers,
        body: JSON.stringify(mediaFormData),
      });

      if (!response.ok) {
        try {
          const error = await response.json();
          setErrorMessage(error.message || `Failed to save media (${response.status})`);
        } catch (e) {
          // Response is not JSON (likely HTML error page)
          const text = await response.text();
          console.error('Server error:', text);
          setErrorMessage(`Server error: ${response.status} - ${response.statusText}`);
        }
        return;
      }

      // Try to use server response to update UI immediately (refresh list for consistency)
      try {
        const saved = await response.json();
        await refreshMediaList(headers);
      } catch (err) {
        console.warn('Could not parse save response, falling back to refresh or optimistic update', err);
        try {
          const mediaResponse = await fetch('/api/media/admin/all', { headers });
          const mediaData = await mediaResponse.json();
          if (mediaData.media && Array.isArray(mediaData.media)) {
            setMediaLibrary(normalizeMediaArray(mediaData.media));
          } else if (Array.isArray(mediaData)) {
            setMediaLibrary(normalizeMediaArray(mediaData));
          } else {
            if (editingMedia) {
              setMediaLibrary(prev => prev.map(m => m.id === editingMedia.id ? { ...m, ...mediaFormData } : m));
            } else {
              setMediaLibrary(prev => [...prev, { ...mediaFormData, id: Date.now() }]);
            }
          }
        } catch (err2) {
          console.error('Error refreshing media list after save:', err2);
        }
      }

      setSuccessMessage(editingMedia ? '✅ Media updated successfully!' : '✅ Media added successfully!');
      setShowMediaForm(false);
      setEditingMedia(null);
      setMediaFormData({
        videoName: '',
        videoLink: '',
        videoId: '',
        mediaType: 'video'
      });
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving media:', err);
      setErrorMessage(err.message || 'Failed to save media');
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (window.confirm('Are you sure you want to delete this media?')) {
      const token = localStorage.getItem('session_token');
      const headers = {
        'Content-Type': 'application/json',
        'X-Session-Token': token,
      };

      try {
        const response = await fetch(`/api/media/${mediaId}`, {
          method: 'DELETE',
          headers,
        });

        if (!response.ok) {
          const error = await response.json();
          setErrorMessage(error.message || 'Failed to delete media');
          return;
        }

        // refresh list from server to reflect deletion
        await refreshMediaList(headers);
        setSelectedMediaIds(selectedMediaIds.filter(id => id !== mediaId));
        setSuccessMessage('✅ Media deleted successfully!');
        setErrorMessage('');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        console.error('Delete media error:', err);
        setErrorMessage(err.message || 'Failed to delete media');
      }
    }
  };

  const toggleMediaSelection = (mediaId) => {
    setSelectedMediaIds(prev =>
      prev.includes(mediaId) ? prev.filter(id => id !== mediaId) : [...prev, mediaId]
    );
  };

  const copySelectedLinks = () => {
    if (selectedMediaIds.length === 0) {
      setErrorMessage('Please select at least one media item');
      return;
    }

    const selectedLinks = mediaLibrary
      .filter(m => selectedMediaIds.includes(m.id))
      .map(m => m.videoLink)
      .join(', ');

    copyToClipboard(selectedLinks);
  };

  const copySelectedIds = () => {
    if (selectedMediaIds.length === 0) {
      setErrorMessage('Please select at least one media item');
      return;
    }

    const selectedVideoIds = mediaLibrary
      .filter(m => selectedMediaIds.includes(m.id))
      .map(m => m.videoId || '')
      .filter(Boolean);

    if (selectedVideoIds.length === 0) {
      setErrorMessage('No video IDs found for the selected items');
      return;
    }

    copyToClipboard(selectedVideoIds.join(', '));
  };

  const filteredMediaLibrary = mediaLibrary.filter((media) => {
    const matchesType = mediaTypeFilter === 'all' || media.mediaType === mediaTypeFilter;
    if (!matchesType) return false;

    if (!mediaSearchQuery) return true;

    const normalizedSearch = mediaSearchQuery.toLowerCase();
    const nameMatch = (media.videoName || '').toLowerCase().includes(normalizedSearch);
    const linkMatch = (media.videoLink || '').toLowerCase().includes(normalizedSearch);
    const idMatch = String(media.id || '').toLowerCase().includes(normalizedSearch);
    const typeMatch = (media.mediaType || '').toLowerCase().includes(normalizedSearch);

    return nameMatch || linkMatch || idMatch || typeMatch;
  });

  const sortedMediaLibrary = [...filteredMediaLibrary].sort((a, b) => {
    const getKey = (item) => {
      if (mediaSortKey === 'type') return item.mediaType || '';
      if (mediaSortKey === 'name') return item.videoName?.toLowerCase() || '';
      if (mediaSortKey === 'id') return String(item.id || '');
      return item.mediaType || '';
    };

    const left = getKey(a);
    const right = getKey(b);
    const compare = left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
    if (compare !== 0) {
      return mediaSortDirection === 'asc' ? compare : -compare;
    }
    return (a.videoName || '').localeCompare(b.videoName || '', undefined, { sensitivity: 'base' });
  });

  const mediaPageCount = Math.max(1, Math.ceil(sortedMediaLibrary.length / MEDIA_ROWS_PER_PAGE));
  const currentMediaPageItems = sortedMediaLibrary.slice((mediaPage - 1) * MEDIA_ROWS_PER_PAGE, mediaPage * MEDIA_ROWS_PER_PAGE);

  useEffect(() => {
    setMediaPage(1);
    setSelectedMediaIds([]);
  }, [mediaTypeFilter, mediaSortKey, mediaSortDirection, mediaLibrary.length]);

  const handleSendEventInvitation = async (e) => {
    e.preventDefault();
    
    if (!selectedEventForInvite) {
      setErrorMessage('Please select an event');
      return;
    }

    if (selectedUsersForInvite.length === 0) {
      setErrorMessage('Please select at least one user');
      return;
    }

    const token = localStorage.getItem('session_token');
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-Token': token,
    };

    try {
      const response = await fetch('/api/events/send-invitations', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          eventId: selectedEventForInvite,
          userEmails: selectedUsersForInvite,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        setErrorMessage(error.message || 'Failed to send invitations');
        return;
      }

      const event = events.find(e => e.id === parseInt(selectedEventForInvite));
      
      // Generate personalized registration link for the first selected user
      // (can be used as template - admin can personalize for each user)
      const firstUserEmail = selectedUsersForInvite[0];
      
      // Debug: log the values
      console.log('🔗 Generating invite link:', {
        eventId: selectedEventForInvite,
        email: firstUserEmail,
        eventName: event?.name
      });
      
      const personalizedLink = generateInviteLink(selectedEventForInvite, firstUserEmail);
      console.log('✅ Generated link:', personalizedLink);
      setEventInviteLink(personalizedLink);
      
      setSuccessMessage(`✅ Event invitations sent to ${selectedUsersForInvite.length} user(s) for "${event?.name}"! Use the link below for registration.`);
      setErrorMessage('');
      // Keep the modal open to show the link for copying
      // Don't clear the form yet
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      console.error('Send invitation error:', err);
      setErrorMessage(err.message || 'Failed to send event invitations');
    }
  };

  const toggleUserSelection = (email) => {
    if (selectedUsersForInvite.includes(email)) {
      setSelectedUsersForInvite(selectedUsersForInvite.filter(e => e !== email));
    } else {
      setSelectedUsersForInvite([...selectedUsersForInvite, email]);
    }
  };

  const handleSubmit = (formData) => {
    if (editingEvent) {
      handleUpdateEvent({ ...formData, id: editingEvent.id });
    } else {
      handleCreateEvent(formData);
    }
  };

  return (
    <>
      <Navbar />

      <div className="container-fluid py-5" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        {/* Header Section */}
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <h1 style={{ color: '#333', fontWeight: '700' }}>🔐 Admin Dashboard</h1>
              {userEmail && <p className="text-muted" style={{ marginTop: '5px' }}>Welcome, <strong>{userEmail}</strong></p>}
            </div>
            <button
              className="btn btn-outline-danger"
              onClick={handleLogout}
            >
              🚪 Logout
            </button>
          </div>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            ✅ {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage('')}
            ></button>
          </div>
        )}

        {errorMessage && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            ❌ {errorMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setErrorMessage('')}
            ></button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading dashboard...</p>
          </div>
        )}

        {!loading && (
          <>
            {/* Dashboard Statistics */}
            <div className="row g-4 mb-5">
              <div className="col-md-3">
                <div className="card shadow-sm border-0" style={{ borderTop: '4px solid #667eea' }}>
                  <div className="card-body">
                    <h6 className="text-muted mb-2">📅 Total Events</h6>
                    <h2 style={{ color: '#667eea', fontWeight: '700' }}>{events.length}</h2>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card shadow-sm border-0" style={{ borderTop: '4px solid #764ba2' }}>
                  <div className="card-body">
                    <h6 className="text-muted mb-2">👥 Total Users</h6>
                    <h2 style={{ color: '#764ba2', fontWeight: '700' }}>{users.length}</h2>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card shadow-sm border-0" style={{ borderTop: '4px solid #f093fb' }}>
                  <div className="card-body">
                    <h6 className="text-muted mb-2">📋 Total Registrations</h6>
                    <h2 style={{ color: '#f093fb', fontWeight: '700' }}>{registrations.length}</h2>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card shadow-sm border-0" style={{ borderTop: '4px solid #4facfe' }}>
                  <div className="card-body">
                    <h6 className="text-muted mb-2">📺 Total Streams</h6>
                    <h2 style={{ color: '#4facfe', fontWeight: '700' }}>{streams.length}</h2>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card shadow-sm border-0" style={{ borderTop: '4px solid #ff6b6b' }}>
                  <div className="card-body">
                    <h6 className="text-muted mb-2">📝 Total Logs</h6>
                    <h2 style={{ color: '#ff6b6b', fontWeight: '700' }}>{logs.length}</h2>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card shadow-sm border-0" style={{ borderTop: '4px solid #ffa94d' }}>
                  <div className="card-body">
                    <h6 className="text-muted mb-2">👁️ Total Site Visits</h6>
                    <h2 style={{ color: '#ffa94d', fontWeight: '700' }}>{siteVisits.toLocaleString()}</h2>
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Tabs */}
            <div className="mb-4">
              <div className="btn-group btn-group-lg" role="group" style={{ flexWrap: 'wrap', gap: '5px' }}>
                <button
                  type="button"
                  className={`btn ${activeTab === 'overview' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab('overview')}
                >
                  📊 Overview
                </button>
                <button
                  type="button"
                  className={`btn ${activeTab === 'events' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab('events')}
                >
                  📅 Events ({events.length})
                </button>
                <button
                  type="button"
                  className={`btn ${activeTab === 'users' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab('users')}
                >
                  👥 Users ({users.length})
                </button>
                <button
                  type="button"
                  className={`btn ${activeTab === 'registrations' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab('registrations')}
                >
                  📋 Registrations ({registrations.length})
                </button>
                <button
                  type="button"
                  className={`btn ${activeTab === 'streams' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab('streams')}
                >
                  📺 Streams ({streams.length})
                </button>
                <button
                  type="button"
                  className={`btn ${activeTab === 'active-streams' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab('active-streams')}
                >
                  🟢 Active Streams
                </button>
                <button
                  type="button"
                  className={`btn ${activeTab === 'site-visits' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab('site-visits')}
                >
                  📊 Site Visits Analytics
                </button>
                <button
                  type="button"
                  className={`btn ${activeTab === 'access-management' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab('access-management')}
                >
                  🔐 Access Management
                </button>
                <button
                  type="button"
                  className={`btn ${activeTab === 'footer' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab('footer')}
                >
                  📄 Footer Links
                </button>
                <button
                  type="button"
                  className={`btn ${activeTab === 'media' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab('media')}
                >
                  🎬 Media Library ({mediaLibrary.length})
                </button>
              </div>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="row g-4">
                <div className="col-12">
                  <div className="card shadow-sm border-0">
                    <div className="card-header bg-primary text-white">
                      <h5 className="card-title mb-0">📊 Admin Control Panel</h5>
                    </div>
                    <div className="card-body">
                      <p>Welcome to your admin control panel! Here you can manage:</p>
                      <ul style={{ fontSize: '1.1rem' }}>
                        <li><strong>Events Management:</strong> Create, edit, and delete events</li>
                        <li><strong>User Management:</strong> View and manage registered users</li>
                        <li><strong>Registration Management:</strong> Monitor and manage event registrations</li>
                        <li><strong>Stream Management:</strong> Manage live streams and recordings</li>
                      </ul>
                      <p className="mt-3">Use the navigation buttons above to access each section.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Events Tab */}
            {activeTab === 'events' && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2>📅 Events Management</h2>
                  <button
                    className="btn btn-success btn-lg"
                    onClick={() => {
                      setEditingEvent(null);
                      setShowForm(!showForm);
                    }}
                  >
                    {showForm ? '❌ Cancel' : '➕ Create Event'}
                  </button>
                </div>

                {showForm && (
                  <div className="card shadow-sm mb-5 border-0" style={{ maxHeight: 'auto', overflowY: 'auto' }}>
                    <div className="card-body">
                      <EventForm
                        initialEvent={editingEvent}
                        onSubmit={async (formData) => {
                          const token = localStorage.getItem('session_token');
                          const headers = {
                            'Content-Type': 'application/json',
                            'X-Session-Token': token,
                          };

                          try {
                            if (editingEvent?.id) {
                              // Update event
                              const response = await fetch(`/api/events/${editingEvent.id}`, {
                                method: 'PUT',
                                headers,
                                body: JSON.stringify(formData),
                              });

                              if (!response.ok) {
                                const error = await response.json();
                                setErrorMessage(error.message || 'Failed to update event');
                                return;
                              }

                              // Refresh events list from server
                              const eventsResponse = await fetch('/api/events/admin/all', { headers });
                              const eventsData = await eventsResponse.json();
                              if (Array.isArray(eventsData)) {
                                setEvents(eventsData);
                              } else if (eventsData.events && Array.isArray(eventsData.events)) {
                                setEvents(eventsData.events);
                              }
                              
                              setSuccessMessage('✅ Event updated successfully!');
                            } else {
                              // Create event
                              const response = await fetch('/api/events', {
                                method: 'POST',
                                headers,
                                body: JSON.stringify(formData),
                              });

                              if (!response.ok) {
                                const error = await response.json();
                                setErrorMessage(error.message || 'Failed to create event');
                                return;
                              }

                              // Refresh events list from server
                              const eventsResponse = await fetch('/api/events/admin/all', { headers });
                              const eventsData = await eventsResponse.json();
                              if (Array.isArray(eventsData)) {
                                setEvents(eventsData);
                              } else if (eventsData.events && Array.isArray(eventsData.events)) {
                                setEvents(eventsData.events);
                              }
                              
                              setSuccessMessage('✅ Event created successfully!');
                            }
                            
                            setShowForm(false);
                            setEditingEvent(null);
                            setErrorMessage('');
                            setTimeout(() => setSuccessMessage(''), 3000);
                          } catch (err) {
                            console.error('Error:', err);
                            setErrorMessage(err.message || 'An error occurred');
                          }
                        }}
                        onCancel={() => {
                          setShowForm(false);
                          setEditingEvent(null);
                        }}
                      />
                    </div>
                  </div>
                )}

                <section>
                  <h3 className="mb-4">All Events ({events.length})</h3>
                  {events.length === 0 ? (
                    <div className="alert alert-info">
                      📌 No events yet. Click "Create Event" to add your first event!
                    </div>
                  ) : (
                    <div className="row g-4">
                      {events.map((event) => (
                        <div key={event.id} className="col-md-6 col-lg-4">
                          <EventCard
                            event={event}
                            isAdmin={true}
                            onEdit={(e) => {
                              setEditingEvent(e);
                              setShowForm(true);
                            }}
                            onDelete={async (id) => {
                              if (window.confirm('Are you sure you want to delete this event?')) {
                                const token = localStorage.getItem('session_token');
                                const headers = {
                                  'Content-Type': 'application/json',
                                  'X-Session-Token': token,
                                };

                                try {
                                  const response = await fetch(`/api/events/${id}`, {
                                    method: 'DELETE',
                                    headers,
                                  });

                                  if (!response.ok) {
                                    const error = await response.json();
                                    setErrorMessage(error.message || 'Failed to delete event');
                                    return;
                                  }

                                  setEvents(events.filter(e => e.id !== id));
                                  setSuccessMessage('✅ Event deleted successfully!');
                                  setErrorMessage('');
                                  setTimeout(() => setSuccessMessage(''), 3000);
                                } catch (err) {
                                  console.error('Delete error:', err);
                                  setErrorMessage(err.message || 'Failed to delete event');
                                }
                              }
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="card shadow-sm border-0">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">👥 Registered Users ({users.length})</h5>
                  <button
                    className="btn btn-light btn-sm"
                    onClick={() => setShowInviteModal(true)}
                    title="Invite a new user to register"
                  >
                    📧 Invite User
                  </button>
                </div>
                <div className="card-body">
                  {users.length > 0 && (
                    <div className="mb-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="🔍 Search users by name or email..."
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                      />
                    </div>
                  )}
                  {users.length === 0 ? (
                    <div className="alert alert-info">
                      📌 No users registered yet.
                    </div>
                  ) : getFilteredUsers().length === 0 ? (
                    <div className="alert alert-warning">
                      ❌ No users match your search query.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Scanner</th>
                            <th>Joined</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getFilteredUsers().map((user) => (
                            <tr key={user.id}>
                              <td><strong>{user.name || (user.email ? user.email.split('@')[0] : 'Unknown')}</strong></td>
                              <td>{user.email}</td>
                              <td>
                                <select
                                  className={`form-select form-select-sm ${user.role === 'admin' ? 'bg-danger bg-opacity-10' : 'bg-info bg-opacity-10'}`}
                                  value={user.role || 'user'}
                                  onChange={(e) => handleUpdateUserRole(user.id, e.target.value)}
                                  style={{ maxWidth: '120px' }}
                                >
                                  <option value="user">👤 User</option>
                                  <option value="admin">👨‍💼 Admin</option>
                                </select>
                              </td>
                              <td>
                                <span className={`badge ${user.isActive === false ? 'bg-secondary' : 'bg-success'}`}>
                                  {user.isActive === false ? 'Inactive' : 'Active'}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${userHasPermission(user, 'ticket.scanner') ? 'bg-success' : 'bg-secondary'}`}>
                                  {userHasPermission(user, 'ticket.scanner') ? 'Scanner Access' : 'No Scanner'}
                                </span>
                              </td>
                              <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</td>
                              <td>
                                <div className="btn-group btn-group-sm" role="group">
                                  <button
                                    className="btn btn-outline-primary"
                                    onClick={() => handleEditUser(user)}
                                    title="Edit user info"
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => openUserPermissions(user)}
                                    title="Manage all permissions for this user"
                                  >
                                    Permissions
                                  </button>
                                  <button
                                    className="btn btn-outline-danger"
                                    onClick={() => handleDeleteUser(user.id, user.email)}
                                    title="Delete user"
                                  >
                                    🗑️ Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* User Permissions Modal */}
            {showUserPermissionsModal && (
              <UserPermissionsModal
                isOpen={showUserPermissionsModal}
                onClose={() => setShowUserPermissionsModal(false)}
                user={selectedUserForPermissions}
                onUpdated={() => {
                  refreshScannerPermissionUserIds();
                  // Also refresh users list from server to reflect any permission-driven changes
                  const token = localStorage.getItem('session_token');
                  if (token) {
                    fetch('/api/users/admin/all', { headers: { 'Content-Type': 'application/json', 'X-Session-Token': token } })
                      .then(r => r.json()).then(data => {
                        if (data && data.users) setUsers(data.users);
                      }).catch(() => {});
                  }
                }}
              />
            )}

            {/* Edit User Modal */}
            {editingUser && (
              <div className="modal d-block" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Edit User Information</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setEditingUser(null)}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <form onSubmit={(e) => handleSaveUserChanges(e)}>
                        <div className="mb-3">
                          <label htmlFor="userName" className="form-label">Full Name</label>
                          <input
                            type="text"
                            className="form-control"
                            id="userName"
                            value={editingUser.name || ''}
                            onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                            placeholder="Enter user name"
                          />
                        </div>
                        <div className="mb-3">
                          <label htmlFor="userEmail" className="form-label">Email</label>
                          <input
                            type="email"
                            className="form-control"
                            id="userEmail"
                            value={editingUser.email || ''}
                            onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                            placeholder="Enter email"
                            autoComplete="email"
                          />
                        </div>
                        <div className="mb-3">
                          <label htmlFor="userRole" className="form-label">Role & Access</label>
                          <select
                            className="form-select"
                            id="userRole"
                            value={editingUser.role || 'user'}
                            onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                          >
                            <option value="user">👤 User</option>
                            <option value="admin">👨‍💼 Admin</option>
                          </select>
                        </div>
                        <div className="mb-3">
                          <label htmlFor="userStatus" className="form-label">Account Status</label>
                          <select
                            className="form-select"
                            id="userStatus"
                            value={editingUser.isActive ? 'active' : 'inactive'}
                            onChange={(e) => setEditingUser({ ...editingUser, isActive: e.target.value === 'active' })}
                          >
                            <option value="active">✅ Active</option>
                            <option value="inactive">⛔ Inactive</option>
                          </select>
                        </div>
                        <div className="d-flex gap-2">
                          <button type="submit" className="btn btn-primary">
                            💾 Save Changes
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setEditingUser(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Invite User Modal */}
            {showInviteModal && (
              <div className="modal d-block" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">📧 Invite User to Register</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowInviteModal(false)}
                      ></button>
                    </div>
                    <div className="modal-body">
                      {!registrationLink ? (
                        <form onSubmit={handleInviteUser}>
                          <div className="mb-3">
                            <label htmlFor="inviteEmail" className="form-label">Email Address</label>
                            <input
                              type="email"
                              className="form-control"
                              id="inviteEmail"
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                              placeholder="Enter email to invite"
                              required
                              autoComplete="email"
                            />
                          </div>
                          <div className="mb-3">
                            <label htmlFor="inviteMethod" className="form-label">Invitation Method</label>
                            <select
                              className="form-select"
                              id="inviteMethod"
                              value={inviteMethod}
                              onChange={(e) => setInviteMethod(e.target.value)}
                            >
                              <option value="email">📧 Send Email Invitation</option>
                              <option value="link">🔗 Copy Registration Link</option>
                            </select>
                          </div>
                          
                          {inviteMethod === 'link' && (
                            <div className="mb-3 p-3 bg-light border rounded">
                              <p className="text-muted mb-2">Registration link preview:</p>
                              <div className="input-group">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={`${window.location.origin}/register`}
                                  readOnly
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-primary"
                                  onClick={() => copyToClipboard(`${window.location.origin}/register`)}
                                >
                                  📋 Copy
                                </button>
                              </div>
                            </div>
                          )}

                          <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-primary">
                              {inviteMethod === 'email' ? '📧 Send Invitation' : '✓ Generate Link'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => {
                                setShowInviteModal(false);
                                setRegistrationLink('');
                                setInviteEmail('');
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      ) : (
                        <div className="text-center">
                          <div className="alert alert-success">
                            ✅ Registration link generated successfully!
                          </div>
                          <p className="text-muted mb-3">Share this link with the user:</p>
                          <div className="input-group mb-3">
                            <input
                              type="text"
                              className="form-control"
                              value={registrationLink}
                              readOnly
                            />
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => copyToClipboard(registrationLink)}
                            >
                              📋 Copy Link
                            </button>
                          </div>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                              setShowInviteModal(false);
                              setRegistrationLink('');
                              setInviteEmail('');
                            }}
                          >
                            Done
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Registrations Tab */}
            {activeTab === 'registrations' && (
              <div className="card shadow-sm border-0">
                <div className="card-header bg-primary text-white">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">📋 Event Registrations ({registrations.length})</h5>
                    <div className="d-flex gap-2">
                      <a
                        href="/admin/scanner"
                        className="btn btn-sm btn-dark"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open QR Ticket Scanner"
                      >
                        📷 Open Scanner
                      </a>
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => setShowEventInviteModal(true)}
                        title="Send event invitations to users"
                      >
                        📧 Event Invitation
                      </button>
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => handleExportRegistrationsCSV()}
                        title="Export registrations to CSV"
                      >
                        📥 Export CSV
                      </button>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  {registrations.length === 0 ? (
                    <div className="alert alert-info">
                      📌 No registrations yet.
                    </div>
                  ) : (
                    <>
                      {/* Filter Section */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <label htmlFor="eventFilter" className="form-label fw-bold">Filter by Event:</label>
                          <select
                            id="eventFilter"
                            className="form-select"
                            value={filterEventId}
                            onChange={(e) => setFilterEventId(e.target.value)}
                          >
                            <option value="">📅 All Events</option>
                            {[...new Set(registrations.map(r => r.eventId))].map(eventId => {
                              const event = events.find(e => e.id === eventId);
                              return (
                                <option key={eventId} value={eventId}>
                                  {event?.name || `Event ${eventId}`}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="statusFilter" className="form-label fw-bold">Filter by Status:</label>
                          <select
                            id="statusFilter"
                            className="form-select"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                          >
                            <option value="">📊 All Status</option>
                            <option value="registered">✅ Registered</option>
                            <option value="attended">🎉 Attended</option>
                            <option value="cancelled">❌ Cancelled</option>
                          </select>
                        </div>
                      </div>

                      {/* Search and Statistics */}
                      <div className="mb-4">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="🔍 Search registrations by user email, name, or event..."
                          value={registrationSearchQuery}
                          onChange={(e) => setRegistrationSearchQuery(e.target.value)}
                        />
                      </div>

                      {/* Statistics Row */}
                      <div className="row mb-4 g-3">
                        <div className="col-md-3">
                          <div className="card border-0 bg-success bg-opacity-10">
                            <div className="card-body text-center">
                              <h6 className="text-muted mb-2">Admin Approved</h6>
                              <h3 className="text-success">{registrations.filter(r => r.isAdminApproved).length}</h3>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="card border-0 bg-warning bg-opacity-10">
                            <div className="card-body text-center">
                              <h6 className="text-muted mb-2">Pending Approval</h6>
                              <h3 className="text-warning">{registrations.filter(r => !r.isAdminApproved).length}</h3>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="card border-0 bg-info bg-opacity-10">
                            <div className="card-body text-center">
                              <h6 className="text-muted mb-2">Verified Tickets</h6>
                              <h3 className="text-info">{registrations.filter(r => r.isVerified).length}</h3>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="card border-0 bg-secondary bg-opacity-10">
                            <div className="card-body text-center">
                              <h6 className="text-muted mb-2">Not Verified</h6>
                              <h3 className="text-secondary">{registrations.filter(r => !r.isVerified).length}</h3>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="card border-0 bg-info bg-opacity-10">
                            <div className="card-body text-center">
                              <h6 className="text-muted mb-2">Attended</h6>
                              <h3 className="text-info">{registrations.filter(r => r.status === 'attended').length}</h3>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="card border-0 bg-danger bg-opacity-10">
                            <div className="card-body text-center">
                              <h6 className="text-muted mb-2">Cancelled</h6>
                              <h3 className="text-danger">{registrations.filter(r => r.status === 'cancelled').length}</h3>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Registrations Table */}
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Event</th>
                              <th>User Email</th>
                              <th>Admin Approval</th>
                              <th>Ticket Verification</th>
                              <th>Registration Status</th>
                              <th>Registered Date</th>
                              <th>Attended</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getFilteredRegistrations().map((reg) => (
                              <tr key={reg.id}>
                                <td><strong>{reg.eventName || 'N/A'}</strong></td>
                                <td>{reg.userEmail}</td>
                                <td>
                                  <div className="d-flex flex-column gap-2">
                                    <span className={`badge ${reg.isAdminApproved ? 'bg-success' : 'bg-warning text-dark'}`}>
                                      {reg.isAdminApproved ? 'Approved' : 'Not Approved'}
                                    </span>
                                    <div className="d-grid">
                                      {reg.isAdminApproved ? (
                                        <button 
                                          className="btn btn-sm btn-danger fw-bold w-100"
                                          onClick={() => handleToggleAdminApproval(reg.id, false)}
                                          title="Click to unapprove this registration"
                                        >
                                          ❌ Unapprove
                                        </button>
                                      ) : (
                                        <button 
                                          className="btn btn-sm btn-warning fw-bold w-100 text-dark"
                                          onClick={() => handleToggleAdminApproval(reg.id, true)}
                                          title="Click to approve registration for scanning"
                                        >
                                          ⏳ Approve Now
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex flex-column gap-2">
                                    <span className={`badge ${reg.isVerified ? 'bg-success' : 'bg-secondary text-white'}`}>
                                      {reg.isVerified ? 'Verified' : 'Not Verified'}
                                    </span>
                                    <div className="d-grid">
                                      {reg.isVerified ? (
                                        <button
                                          className="btn btn-sm btn-danger fw-bold w-100"
                                          onClick={() => handleToggleTicketVerification(reg.id, false)}
                                          title="Click to unverify this ticket"
                                        >
                                          ❌ Unverify
                                        </button>
                                      ) : (
                                        <button
                                          className="btn btn-sm btn-success fw-bold w-100"
                                          onClick={() => handleToggleTicketVerification(reg.id, true)}
                                          title="Click to manually verify this ticket"
                                        >
                                          ✅ Verify Ticket
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <select
                                    className="form-select form-select-sm"
                                    value={reg.status || 'registered'}
                                    onChange={(e) => handleUpdateRegistrationStatus(reg.id, e.target.value)}
                                    style={{ maxWidth: '130px' }}
                                  >
                                    <option value="registered">✅ Registered</option>
                                    <option value="attended">🎉 Attended</option>
                                    <option value="cancelled">❌ Cancelled</option>
                                  </select>
                                </td>
                                <td>{reg.registeredAt ? new Date(reg.registeredAt).toLocaleDateString() : 'N/A'}</td>
                                <td>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={reg.attended || false}
                                    onChange={(e) => handleToggleAttendance(reg.id, e.target.checked)}
                                    title="Mark as attended"
                                  />
                                </td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteRegistration(reg.id)}
                                  >
                                    🗑️ Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {getFilteredRegistrations().length === 0 && (
                        <div className="alert alert-info mt-3">
                          📌 No registrations match the selected filters.
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Event Invitation Modal */}
            {showEventInviteModal && (
              <div className="modal d-block" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                <div className="modal-dialog modal-dialog-centered modal-lg">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">📧 Send Event Invitation</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => {
                          setShowEventInviteModal(false);
                          setSelectedEventForInvite('');
                          setSelectedUsersForInvite([]);
                          setGuestEmailForInvite('');
                        }}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <form onSubmit={handleSendEventInvitation}>
                        <div className="mb-4">
                          <label htmlFor="eventSelect" className="form-label fw-bold">Select Event *</label>
                          <select
                            id="eventSelect"
                            className="form-select form-select-lg"
                            value={selectedEventForInvite}
                            onChange={(e) => setSelectedEventForInvite(e.target.value)}
                            required
                          >
                            <option value="">-- Choose an event --</option>
                            {events.filter(e => e.status !== 'cancelled').map(event => (
                              <option key={event.id} value={event.id}>
                                📅 {event.name} (Active)
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="mb-4">
                          <label className="form-label fw-bold">Select Users to Invite *</label>
                          
                          {/* Guest Email Input Section */}
                          <div className="mb-3 p-3 bg-light border rounded">
                            <label htmlFor="guestEmail" className="form-label mb-2">
                              ➕ Invite Guest (Non-registered User)
                            </label>
                            <div className="input-group">
                              <input
                                type="email"
                                id="guestEmail"
                                className="form-control"
                                value={guestEmailForInvite}
                                onChange={(e) => setGuestEmailForInvite(e.target.value)}
                                placeholder="guest@example.com"
                                autoComplete="email"
                              />
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => {
                                  if (guestEmailForInvite && guestEmailForInvite.includes('@')) {
                                    if (!selectedUsersForInvite.includes(guestEmailForInvite)) {
                                      setSelectedUsersForInvite([...selectedUsersForInvite, guestEmailForInvite]);
                                      setGuestEmailForInvite('');
                                    } else {
                                      setErrorMessage('This email is already selected');
                                    }
                                  } else {
                                    setErrorMessage('Please enter a valid email address');
                                  }
                                }}
                              >
                                Add
                              </button>
                            </div>
                            <small className="text-muted d-block mt-2">Add an email for someone who isn't yet registered</small>
                          </div>

                          {/* Existing Users Selection */}
                          <div style={{ 
                            maxHeight: '300px', 
                            overflowY: 'auto', 
                            border: '1px solid #ddd', 
                            borderRadius: '5px',
                            padding: '10px'
                          }}>
                            {users.length === 0 ? (
                              <p className="text-muted">No registered users available</p>
                            ) : (
                              users.map(user => (
                                <div key={user.id} className="form-check mb-2">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`user-${user.id}`}
                                    checked={selectedUsersForInvite.includes(user.email)}
                                    onChange={() => toggleUserSelection(user.email)}
                                  />
                                  <label className="form-check-label" htmlFor={`user-${user.id}`}>
                                        <strong>{user.name || (user.email ? user.email.split('@')[0] : 'Unknown')}</strong> ({user.email || 'N/A'})
                                  </label>
                                </div>
                              ))
                            )}
                          </div>

                          {selectedUsersForInvite.length > 0 && (
                            <div className="mt-3">
                              <p className="text-info fw-bold mb-2">
                                ✓ {selectedUsersForInvite.length} email(s) selected:
                              </p>
                              <div className="d-flex flex-wrap gap-2">
                                {selectedUsersForInvite.map((email) => (
                                  <div key={email} className="badge bg-primary d-flex align-items-center gap-2">
                                    {email}
                                    <button
                                      type="button"
                                      className="btn-close btn-close-white"
                                      style={{ fontSize: '0.7rem' }}
                                      onClick={() => setSelectedUsersForInvite(selectedUsersForInvite.filter(e => e !== email))}
                                    ></button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {eventInviteLink && (
                          <div className="mb-4 p-3 bg-success bg-opacity-10 border border-success rounded">
                            <p className="text-success fw-bold mb-2">✅ Registration Link:</p>
                            <div className="input-group">
                              <input
                                type="text"
                                className="form-control"
                                value={eventInviteLink}
                                readOnly
                              />
                              <button
                                type="button"
                                className="btn btn-success"
                                onClick={() => copyToClipboard(eventInviteLink)}
                              >
                                📋 Copy Link
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="d-flex gap-2">
                          <button type="submit" className="btn btn-primary flex-grow-1">
                            📧 Send Invitations ({selectedUsersForInvite.length})
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                              setShowEventInviteModal(false);
                              setSelectedEventForInvite('');
                              setSelectedUsersForInvite([]);
                              setEventInviteLink('');
                              setGuestEmailForInvite('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Streams Tab */}
            {activeTab === 'streams' && (
              <>
                <h2 className="mb-4">📺 Stream Management</h2>
                <StreamManagement />
              </>
            )}

            {/* Active Streams Tab */}
            {activeTab === 'active-streams' && (
              <>
                <h2 className="mb-4">🟢 Active Streams Management</h2>
                <ActiveStreamsManagement />
              </>
            )}

            {/* Site Visits Analytics Tab */}
            {activeTab === 'site-visits' && (
              <>
                <SiteVisitsAnalytics />
              </>
            )}

            {/* Access Management Tab */}
            {activeTab === 'access-management' && (
              <>
                <PermissionManagement />
              </>
            )}

            {/* Footer Links Tab */}
            {activeTab === 'footer' && (
              <>
                <FooterManagement />
              </>
            )}

            {/* Media Library Tab */}
            {activeTab === 'media' && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2>🎬 Media Library Management</h2>
                  <button
                    className="btn btn-success btn-lg"
                    onClick={() => {
                      setEditingMedia(null);
                      setMediaFormData({
                        videoName: '',
                        videoLink: '',
                        videoId: '',
                        mediaType: 'video'
                      });
                      setShowMediaForm(!showMediaForm);
                    }}
                  >
                    {showMediaForm ? '❌ Cancel' : '➕ Add Media'}
                  </button>
                </div>

                {showMediaForm && (
                  <div className="card shadow-sm mb-5 border-0">
                    <div className="card-header bg-info text-white">
                      <h5 className="mb-0">{editingMedia ? '✏️ Edit Media' : '➕ Add New Media'}</h5>
                    </div>
                    <div className="card-body">
                      <form onSubmit={handleAddMedia}>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label htmlFor="videoName" className="form-label fw-bold">Video/Music Name *</label>
                            <input
                              type="text"
                              className="form-control"
                              id="videoName"
                              value={mediaFormData.videoName}
                              onChange={(e) => setMediaFormData({ ...mediaFormData, videoName: e.target.value })}
                              placeholder="e.g., Summer Playlist 2025"
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="videoId" className="form-label fw-bold">Video ID</label>
                            <input
                              type="text"
                              className="form-control"
                              id="videoId"
                              value={mediaFormData.videoId}
                              onChange={(e) => setMediaFormData({ ...mediaFormData, videoId: e.target.value })}
                              placeholder="e.g., dQw4w9WgXcQ"
                            />
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-8">
                            <label htmlFor="videoLink" className="form-label fw-bold">Video/Music Link (Optional)</label>
                            <input
                              type="url"
                              className="form-control"
                              id="videoLink"
                              value={mediaFormData.videoLink}
                              onChange={(e) => setMediaFormData({ ...mediaFormData, videoLink: e.target.value })}
                              placeholder="https://youtube.com/watch?v=dQw4w9WgXcQ (optional)"
                            />
                          </div>
                          <div className="col-md-4">
                            <label htmlFor="mediaType" className="form-label fw-bold">Media Type *</label>
                            <select
                              className="form-select"
                              id="mediaType"
                              value={mediaFormData.mediaType}
                              onChange={(e) => setMediaFormData({ ...mediaFormData, mediaType: e.target.value })}
                            >
                              <option value="video">🎬 Video</option>
                              <option value="music">🎵 Music</option>
                              <option value="img">🖼️ Image</option>
                            </select>
                          </div>
                        </div>

                        <div className="d-flex gap-2">
                          <button type="submit" className="btn btn-primary btn-lg flex-grow-1">
                            {editingMedia ? '💾 Update Media' : '➕ Add Media'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-lg"
                            onClick={() => {
                              setShowMediaForm(false);
                              setEditingMedia(null);
                              setMediaFormData({
                                videoName: '',
                                videoLink: '',
                                videoId: '',
                                mediaType: 'video'
                              });
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Selected Media Actions */}
                {selectedMediaIds.length > 0 && (
                  <div className="card shadow-sm mb-4 border-warning bg-warning bg-opacity-10">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0">
                            ✓ {selectedMediaIds.length} media item(s) selected
                          </h6>
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-primary"
                            onClick={copySelectedLinks}
                            title="Copy all selected video links separated by commas"
                          >
                            📋 Copy Selected Links
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={copySelectedIds}
                            title="Copy all selected video IDs separated by commas"
                          >
                            🆔 Copy Selected IDs
                          </button>
                          <button
                            className="btn btn-warning"
                            onClick={() => setSelectedMediaIds([])}
                          >
                            Clear Selection
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Media Library Table */}
                <section>
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-3">
                    <div>
                      <h3 className="mb-2">All Media ({sortedMediaLibrary.length})</h3>
                      <p className="mb-0 text-muted">Showing {currentMediaPageItems.length} of {sortedMediaLibrary.length} item{sortedMediaLibrary.length === 1 ? '' : 's'} — 50 rows per page.</p>
                    </div>
                    <div className="d-flex flex-wrap gap-2 align-items-end">
                      <div>
                        <label className="form-label small mb-1">Search media</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by name, type or ID"
                          value={mediaSearchTerm}
                          onChange={(e) => {
                            const value = e.target.value;
                            setMediaSearchTerm(value);
                            setMediaSearchQuery(value.trim());
                          }}
                        />
                      </div>
                      <div>
                        <label className="form-label small mb-1">Filter by type</label>
                        <select
                          className="form-select"
                          value={mediaTypeFilter}
                          onChange={(e) => setMediaTypeFilter(e.target.value)}
                        >
                          <option value="all">All types</option>
                          <option value="video">Video</option>
                          <option value="music">Music</option>
                          <option value="img">Image</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label small mb-1">Sort by</label>
                        <select
                          className="form-select"
                          value={mediaSortKey}
                          onChange={(e) => setMediaSortKey(e.target.value)}
                        >
                          <option value="type">Type</option>
                          <option value="name">Name</option>
                          <option value="id">ID</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label small mb-1">Direction</label>
                        <select
                          className="form-select"
                          value={mediaSortDirection}
                          onChange={(e) => setMediaSortDirection(e.target.value)}
                        >
                          <option value="asc">Ascending</option>
                          <option value="desc">Descending</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {sortedMediaLibrary.length === 0 ? (
                    <div className="alert alert-info">
                      📌 No media items match this filter. Try another media type or add new media.
                    </div>
                  ) : (
                    <div className="card shadow-sm border-0">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: '40px' }}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedMediaIds.length === currentMediaPageItems.length && currentMediaPageItems.length > 0}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedMediaIds(currentMediaPageItems.map(m => m.id));
                                    } else {
                                      setSelectedMediaIds([]);
                                    }
                                  }}
                                  title="Select all"
                                />
                              </th>
                              <th>ID</th>
                              <th>Media Name</th>
                              <th>Video Link</th>
                              <th>Video ID</th>
                              <th>Type</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentMediaPageItems.map((media) => (
                              <tr key={media.id}>
                                <td>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedMediaIds.includes(media.id)}
                                    onChange={() => toggleMediaSelection(media.id)}
                                  />
                                </td>
                                <td><strong>#{media.id}</strong></td>
                                <td>{media.videoName || '—'}</td>
                                <td>
                                  {media.videoLink ? (() => {
                                    const linkText = String(media.videoLink);
                                    return (
                                      <a
                                        href={media.videoLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-truncate d-inline-block"
                                        style={{ maxWidth: '250px' }}
                                        title={linkText}
                                      >
                                        🔗 {linkText.length > 40 ? `${linkText.substring(0, 40)}...` : linkText}
                                      </a>
                                    );
                                  })() : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                                <td>{media.videoId || 'N/A'}</td>
                                <td>
                                  <span className={`badge ${
                                    media.mediaType === 'video' ? 'bg-primary' :
                                    media.mediaType === 'music' ? 'bg-success' :
                                    'bg-secondary'
                                  }`}>
                                    {media.mediaType === 'video' ? '🎬 Video' :
                                     media.mediaType === 'music' ? '🎵 Music' :
                                     '🖼️ Image'}
                                  </span>
                                </td>
                                <td>
                                  <div className="btn-group btn-group-sm" role="group">
                                    <button
                                      className="btn btn-outline-primary"
                                      onClick={() => {
                                        setEditingMedia(media);
                                        setMediaFormData({
                                          videoName: media.videoName,
                                          videoLink: media.videoLink,
                                          videoId: media.videoId || '',
                                          mediaType: media.mediaType
                                        });
                                        setShowMediaForm(true);
                                      }}
                                      title="Edit media"
                                    >
                                      ✏️ Edit
                                    </button>
                                    <button
                                      className="btn btn-outline-success"
                                      onClick={() => copyToClipboard(media.videoLink)}
                                      title="Copy link to clipboard"
                                    >
                                      📋 Copy
                                    </button>
                                    <button
                                      className="btn btn-outline-danger"
                                      onClick={() => handleDeleteMedia(media.id)}
                                      title="Delete media"
                                    >
                                      🗑️ Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 p-3">
                        <small className="text-muted">Page {mediaPage} of {mediaPageCount}</small>
                        <div className="btn-group" role="group">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            disabled={mediaPage <= 1}
                            onClick={() => setMediaPage((prev) => Math.max(1, prev - 1))}
                          >
                            ← Prev
                          </button>
                          {Array.from({ length: mediaPageCount }, (_, index) => (
                            <button
                              key={`page-${index + 1}`}
                              type="button"
                              className={`btn btn-outline-secondary ${mediaPage === index + 1 ? 'active' : ''}`}
                              onClick={() => setMediaPage(index + 1)}
                            >
                              {index + 1}
                            </button>
                          ))}
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            disabled={mediaPage >= mediaPageCount}
                            onClick={() => setMediaPage((prev) => Math.min(mediaPageCount, prev + 1))}
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </section>
              </>
            )}
          </>
        )}
      </div>
      <AdSense />
    </>
  );
}

export default AdminDashboard;
